import React, { useState } from "react";
import localizationStrings from "../../statics/localization";
import { getProfileCopy } from "../profileSettings/profileCopy";
import { defaultProfileAvatar } from "../../statics/config";
import { normalizeApiText } from "../../utils/charset";
import CinemaIcon from "../cinema/CinemaIcon";
import TvLockIcon from "../../assets/icons/svg/TvLockIcon";

export default function ProfilePinForm({
  profile,
  avatar,
  value,
  onChange,
  onSubmit,
  onCancel,
  loading,
  canCancel,
  editing,
  error,
  note,
  submitTrackId,
  cancelTrackId,
  idPrefix = "profile-access"
}) {
  const copy = getProfileCopy();
  const [focused, setFocused] = useState(false);
  const needsPin = !!profile?.hasPin;
  return (
    <section
      className={`profile-access${loading ? " is-loading" : ""}`}
      aria-labelledby={`${idPrefix}-title`}
      aria-busy={loading}
    >
      <div className="profile-access-identity">
        <div className="profile-access-avatar">
          <img
            src={avatar || defaultProfileAvatar}
            alt=""
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = defaultProfileAvatar;
            }}
          />
          {needsPin && (
            <span className="profile-access-lock" aria-hidden="true">
              <TvLockIcon />
            </span>
          )}
        </div>
        <p className="profile-access-name">{normalizeApiText(profile?.name)}</p>
      </div>
      <h1 id={`${idPrefix}-title`}>
        {loading
          ? localizationStrings.profile.logging
          : editing
          ? copy.pinEditTitle
          : copy.pinTitle}
      </h1>
      <p className="profile-access-note">
        {loading
          ? copy.pinLoading
          : note
          ? note
          : editing
          ? copy.pinEditNote
          : needsPin
          ? copy.pinNote
          : copy.intro}
      </p>
      {loading ? (
        <div
          className="profile-access-progress"
          role="status"
          aria-label={localizationStrings.profile.logging}
        >
          <span />
        </div>
      ) : (
        <form
          id={idPrefix === "profile-access" ? "profileListForm" : `${idPrefix}-form`}
          className="profile-access-form"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
          noValidate
        >
          {needsPin && (
            <div className="profile-pin-field">
              <label htmlFor={`${idPrefix}-pin`}>
                {localizationStrings.login.enterPIN}
              </label>
              <div className="profile-pin-control">
                <input
                  id={`${idPrefix}-pin`}
                  name="profilePin"
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  autoFocus
                  maxLength={4}
                  pattern="[0-9]{4}"
                  required
                  value={value}
                  aria-invalid={!!error}
                  aria-describedby={
                    error ? `${idPrefix}-error` : `${idPrefix}-hint`
                  }
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  onChange={(event) =>
                    onChange(event.target.value.replace(/\D/g, "").slice(0, 4))
                  }
                />
                <div className="profile-pin-digits" aria-hidden="true">
                  {[0, 1, 2, 3].map((index) => (
                    <span
                      key={index}
                      className={`${index < value.length ? "filled " : ""}${
                        focused && index === Math.min(value.length, 3)
                          ? "focused"
                          : ""
                      }`}
                    >
                      {index < value.length ? "•" : ""}
                    </span>
                  ))}
                </div>
              </div>
              <p id={`${idPrefix}-hint`} className="profile-pin-hint">
                {copy.pinHint}
              </p>
            </div>
          )}
          {error && (
            <p
              id={`${idPrefix}-error`}
              className="profile-pin-error"
              role="alert"
            >
              {error}
            </p>
          )}
          <button
            type="submit"
            data-track-id={submitTrackId}
            className="profile-access-submit"
            disabled={needsPin && value.length !== 4}
          >
            {needsPin ? copy.pinContinue : copy.retry}
            <CinemaIcon name="arrow" />
          </button>
        </form>
      )}
      {(!loading || canCancel) && (
        <button
          type="button"
          data-track-id={cancelTrackId}
          className="profile-access-cancel"
          onClick={onCancel}
        >
          <CinemaIcon name="previous" />
          {localizationStrings.cancel}
        </button>
      )}
    </section>
  );
}
